import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight, X, UserPlus, Calendar, User, MapPin, Clock, Map as MapIcon, List } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";
import CrewMap from "@/components/CrewMap";

const COLUMNS = [
  { id: "todo", label: "To Do" },
  { id: "in_progress", label: "In Progress" },
  { id: "done", label: "Done" },
];

const PRIORITY = {
  high: { label: "High", cls: "bg-[#FF6B6B]/15 text-[#c0392b] border-[#FF6B6B]/40" },
  medium: { label: "Medium", cls: "bg-[#FFC43D]/20 text-[#946200] border-[#FFC43D]/50" },
  low: { label: "Low", cls: "bg-[#2EC4B6]/15 text-[#0d7d72] border-[#2EC4B6]/40" },
};

const emptyTask = () => ({ title: "", assignee: "", assignee_id: null, due_date: "", priority: "medium", status: "todo" });

export default function TaskBoard() {
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const [crew, setCrew] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyTask());
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newMember, setNewMember] = useState("");
  const [checkinView, setCheckinView] = useState("list");
  const [dragId, setDragId] = useState(null);
  const [fAssignee, setFAssignee] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [sortDue, setSortDue] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    try {
      const [t, m, c, ci] = await Promise.all([
        api.get("/board/tasks"), api.get("/team/members"), api.get("/crew"), api.get("/board/checkins"),
      ]);
      setTasks(t.data);
      setTeam(m.data);
      setCrew(c.data);
      setCheckins(ci.data);
    } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = (status = "todo") => { setForm({ ...emptyTask(), status }); setEditId(null); setDialog(true); };
  const openEdit = (t) => { setForm({ title: t.title, assignee: t.assignee || "", assignee_id: t.assignee_id || null, due_date: t.due_date || "", priority: t.priority, status: t.status }); setEditId(t.id); setDialog(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onAssignee = (v) => {
    if (!v) return setForm((f) => ({ ...f, assignee_id: null, assignee: "" }));
    if (v.startsWith("c:")) { const id = v.slice(2); const m = crew.find((x) => x.id === id); setForm((f) => ({ ...f, assignee_id: id, assignee: m ? m.name : "" })); }
    else { setForm((f) => ({ ...f, assignee_id: null, assignee: v.slice(2) })); }
  };
  const assigneeValue = form.assignee_id ? `c:${form.assignee_id}` : (form.assignee ? `t:${form.assignee}` : "");

  const save = async () => {
    if (!form.title.trim()) return toast.error("Task title is required");
    setBusy(true);
    try {
      if (editId) await api.put(`/board/tasks/${editId}`, form);
      else await api.post("/board/tasks", form);
      toast.success(editId ? "Task updated" : "Task added");
      setDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const move = async (t, dir) => {
    const order = ["todo", "in_progress", "done"];
    const idx = order.indexOf(t.status) + dir;
    if (idx < 0 || idx > 2) return;
    const status = order[idx];
    setTasks((ts) => ts.map((x) => (x.id === t.id ? { ...x, status } : x)));
    try { await api.patch(`/board/tasks/${t.id}/status`, { status }); } catch { load(); }
  };

  const dropTo = async (status) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const t = tasks.find((x) => x.id === id);
    if (!t || t.status === status) return;
    setTasks((ts) => ts.map((x) => (x.id === id ? { ...x, status } : x)));
    try { await api.patch(`/board/tasks/${id}/status`, { status }); } catch { load(); }
  };

  const remove = async (t) => { await api.delete(`/board/tasks/${t.id}`); setTasks((ts) => ts.filter((x) => x.id !== t.id)); toast.success("Task removed"); };

  const addMember = async () => {
    const name = newMember.trim();
    if (!name) return;
    try { const { data } = await api.post("/team/members", { name }); setTeam((m) => [...m, data].sort((a, b) => a.name.localeCompare(b.name))); setNewMember(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delMember = async (m) => { await api.delete(`/team/members/${m.id}`); setTeam((x) => x.filter((y) => y.id !== m.id)); };

  return (
    <div data-testid="task-board">
      {/* Team manager */}
      <div className="border border-[var(--line)] bg-white p-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="label-mono text-[var(--text-2)] flex items-center gap-2"><UserPlus className="w-4 h-4" /> Team</span>
          <div className="flex flex-wrap items-center gap-2 flex-1">
            {team.map((m) => (
              <span key={m.id} data-testid={`team-chip-${m.id}`} className="inline-flex items-center gap-1.5 h-8 pl-3 pr-2 rounded-full bg-[#f4f4f2] border border-[var(--line)] text-sm">
                {m.name}
                <button data-testid={`team-del-${m.id}`} onClick={() => delMember(m)} className="text-[var(--text-2)] hover:text-[var(--danger)]"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
            <input
              data-testid="team-add-input"
              value={newMember}
              onChange={(e) => setNewMember(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addMember(); }}
              placeholder="Add member…"
              className="h-8 px-3 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)] w-36"
            />
            <button data-testid="team-add-btn" onClick={addMember} className="h-8 px-3 text-sm border border-[var(--line)] hover:border-[var(--accent)] transition-colors">Add</button>
          </div>
          <button data-testid="board-add-task" onClick={() => openNew()} className="flex items-center gap-2 h-10 px-4 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors shrink-0">
            <Plus className="w-4 h-4" /> Add task
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="board-filters">
        <span className="label-mono text-[var(--text-2)]">Filter</span>
        <select data-testid="filter-assignee" value={fAssignee} onChange={(e) => setFAssignee(e.target.value)} className="h-9 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]">
          <option value="">All assignees</option>
          {team.map((m) => <option key={`t${m.id}`} value={m.name}>{m.name}</option>)}
          {crew.map((m) => <option key={`c${m.id}`} value={m.name}>{m.name}</option>)}
        </select>
        <select data-testid="filter-priority" value={fPriority} onChange={(e) => setFPriority(e.target.value)} className="h-9 px-2 border border-[var(--line)] bg-white text-sm outline-none focus:border-[var(--accent)]">
          <option value="">All priorities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        {(fAssignee || fPriority) && <button data-testid="filter-clear" onClick={() => { setFAssignee(""); setFPriority(""); }} className="text-xs text-[var(--accent)] hover:underline">Clear</button>}
        <button data-testid="sort-due" onClick={() => setSortDue((v) => !v)} className={`h-9 px-3 text-sm border transition-colors ml-auto ${sortDue ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}>Sort by due date{sortDue ? " ✓" : ""}</button>
      </div>

      {/* Board */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          let items = tasks.filter((t) => t.status === col.id && (!fAssignee || t.assignee === fAssignee) && (!fPriority || t.priority === fPriority));
          if (sortDue) items = [...items].sort((a, b) => (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1);
          const overdueCount = items.filter((t) => t.due_date && t.due_date < todayIso && t.status !== "done").length;
          return (
            <div key={col.id} data-testid={`board-column-${col.id}`} onDragOver={(e) => e.preventDefault()} onDrop={() => dropTo(col.id)} className="bg-[#f4f4f2] border border-[var(--line)] min-h-[300px]">
              <div className="flex items-center justify-between px-4 h-12 border-b border-[var(--line)]">
                <h3 className="font-serif text-lg">{col.label}</h3>
                <div className="flex items-center gap-2">
                  {overdueCount > 0 && <span data-testid={`board-overdue-${col.id}`} className="label-mono text-white bg-[var(--danger)] rounded-full px-2 py-0.5 text-[10px]">{overdueCount} overdue</span>}
                  <span className="label-mono text-[var(--text-2)]">{items.length}</span>
                </div>
              </div>
              <div className="p-3 space-y-3">
                {items.length === 0 && <p className="text-xs text-[var(--text-2)] text-center py-8">No tasks</p>}
                {items.map((t) => {
                  const p = PRIORITY[t.priority] || PRIORITY.medium;
                  const overdue = t.due_date && t.due_date < todayIso && t.status !== "done";
                  return (
                    <div key={t.id} data-testid={`board-card-${t.id}`} draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => setDragId(null)} className={`bg-white border p-3 cursor-grab active:cursor-grabbing transition-opacity ${overdue ? "border-[var(--danger)] bg-[rgba(220,38,38,0.05)]" : "border-[var(--line)]"} ${dragId === t.id ? "opacity-40" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${p.cls}`} data-testid={`card-priority-${t.id}`}>{p.label}</span>
                        <div className="flex items-center gap-1">
                          <button data-testid={`board-move-left-${t.id}`} onClick={() => move(t, -1)} disabled={t.status === "todo"} className="p-1 text-[var(--text-2)] hover:text-[var(--accent)] disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                          <button data-testid={`board-move-right-${t.id}`} onClick={() => move(t, 1)} disabled={t.status === "done"} className="p-1 text-[var(--text-2)] hover:text-[var(--accent)] disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <button onClick={() => openEdit(t)} className="block text-left w-full mt-2">
                        <p className="text-sm font-medium leading-snug">{t.title}</p>
                      </button>
                      <div className="flex items-center justify-between mt-3 text-xs text-[var(--text-2)]">
                        <span className="flex items-center gap-1 min-w-0"><User className="w-3 h-3 shrink-0" /> <span className="truncate">{t.assignee || "Unassigned"}</span></span>
                        <div className="flex items-center gap-2 shrink-0">
                          {t.due_date && <span data-testid={`card-due-${t.id}`} className={`flex items-center gap-1 ${overdue ? "text-[var(--danger)] font-semibold" : ""}`}><Calendar className="w-3 h-3" /> {t.due_date}{overdue ? " · overdue" : ""}</span>}
                          <button data-testid={`board-del-${t.id}`} onClick={() => remove(t)} className="hover:text-[var(--danger)]"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Site check-ins from crew */}
      {checkins.length > 0 && (
        <div className="mt-6 border border-[var(--line)] bg-white p-4" data-testid="board-checkins">
          <div className="flex items-center justify-between mb-3">
            <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><MapPin className="w-4 h-4" /> Crew site check-ins</p>
            <div className="inline-flex border border-[var(--line)]">
              <button data-testid="checkins-view-list" onClick={() => setCheckinView("list")} className={`flex items-center gap-1.5 h-8 px-3 text-xs transition-colors ${checkinView === "list" ? "bg-[#1A1A18] text-white" : "text-[var(--text-2)] hover:text-[var(--accent)]"}`}><List className="w-3.5 h-3.5" /> List</button>
              <button data-testid="checkins-view-map" onClick={() => setCheckinView("map")} className={`flex items-center gap-1.5 h-8 px-3 text-xs transition-colors ${checkinView === "map" ? "bg-[#1A1A18] text-white" : "text-[var(--text-2)] hover:text-[var(--accent)]"}`}><MapIcon className="w-3.5 h-3.5" /> Map</button>
            </div>
          </div>
          {checkinView === "map" && <div className="mb-1"><CrewMap checkins={checkins} /></div>}
          {checkinView === "list" && (
          <div className="space-y-2">
            {checkins.map((c) => (
              <div key={c.id} data-testid={`checkin-row-${c.id}`} className="flex items-center gap-3 border border-[var(--line)] bg-[#f9f9f7] p-3 text-sm">
                <MapPin className="w-4 h-4 text-[var(--accent)] shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate"><span className="font-medium">{c.crew_name}</span>{c.task_title ? ` · ${c.task_title}` : " · on-site"}{c.note ? ` — ${c.note}` : ""}</p>
                  <p className="text-xs text-[var(--text-2)] font-mono flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleString()}</p>
                </div>
                {c.photo_url && <a data-testid={`checkin-photo-${c.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} target="_blank" rel="noreferrer" className="shrink-0"><img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="job proof" className="w-11 h-11 object-cover rounded border border-[var(--line)]" /></a>}
                {c.maps_url && <a data-testid={`checkin-map-${c.id}`} href={c.maps_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--accent)] hover:underline shrink-0">View map</a>}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      <Modal open={dialog} onClose={() => setDialog(false)} testid="board-task-dialog" subtitle="Team task board" title={editId ? "Edit task" : "New task"}>
        <div className="space-y-4">
          <div>
            <span className="label-mono text-[var(--text-2)]">Task</span>
            <input data-testid="task-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Mow the Johnson lawn" className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="label-mono text-[var(--text-2)]">Assignee</span>
              <select data-testid="task-assignee" value={assigneeValue} onChange={(e) => onAssignee(e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
                <option value="">Unassigned</option>
                {crew.length > 0 && (
                  <optgroup label="Crew (accounts)">
                    {crew.map((m) => <option key={m.id} value={`c:${m.id}`}>{m.name}{m.active ? "" : " (inactive)"}</option>)}
                  </optgroup>
                )}
                {team.length > 0 && (
                  <optgroup label="Team (names)">
                    {team.map((m) => <option key={m.id} value={`t:${m.name}`}>{m.name}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
            <div>
              <span className="label-mono text-[var(--text-2)]">Due date</span>
              <input type="date" data-testid="task-due" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="label-mono text-[var(--text-2)]">Priority</span>
              <select data-testid="task-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <span className="label-mono text-[var(--text-2)]">Status</span>
              <select data-testid="task-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button data-testid="task-cancel" onClick={() => setDialog(false)} className="flex-1 h-11 border border-[var(--line)] hover:bg-white transition-colors">Cancel</button>
            <button data-testid="board-task-save" onClick={save} disabled={busy} className="flex-1 h-11 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50">{busy ? "Saving…" : "Save task"}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
